/**
 * Sentry error monitoring integration
 *
 * Reads REACT_APP_SENTRY_DSN from the environment.
 * If the DSN is absent or we're in development, Sentry stays disabled
 * so local dev stays noise-free and error-free.
 *
 * Usage:
 *  - Call initSentry() once at the top of index.tsx (before rendering).
 *  - Call captureError(error, context?) anywhere to report an error.
 *  - setUserContext(user) after login to tag events with the user's id/role.
 */

import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint, Scope } from '@sentry/react';

const DSN = process.env.REACT_APP_SENTRY_DSN ?? '';
const ENV = process.env.REACT_APP_ENV ?? process.env.NODE_ENV ?? 'development';
const RELEASE = process.env.REACT_APP_VERSION ?? undefined;
const ENABLED = Boolean(DSN) && ENV !== 'development';

/**
 * Initialise Sentry. Must be called once before React is rendered.
 */
export function initSentry(): void {
  if (!ENABLED) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[Sentry] Disabled in development (no DSN or dev env).');
    }
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    // Capture 10 % of transactions as performance traces in production
    tracesSampleRate: ENV === 'production' ? 0.1 : 1.0,
    // Only report errors from our own origin
    allowUrls: [window.location.origin],
    // Ignore common browser-extension and network noise
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error exception captured',
      'Network Error',
      'Request aborted',
      /^Loading chunk \d+ failed/,
    ],
    beforeSend(event: ErrorEvent, hint: EventHint) {
      // Don't forward 401 / 403 auth errors — they're handled by the axios interceptor
      const error = hint?.originalException;
      if (error && typeof error === 'object' && 'response' in error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 401 || status === 403) return null;
      }
      return event;
    },
  });
}

/**
 * Capture any error and optional contextual metadata.
 */
export function captureError(
  error: unknown,
  context?: Record<string, unknown>
): string | null {
  if (!ENABLED) {
    console.error('[Sentry:captureError]', error, context);
    return null;
  }
  return Sentry.withScope((scope: Scope) => {
    if (context) scope.setExtras(context);
    return Sentry.captureException(error);
  });
}

/**
 * Tag subsequent events with the currently logged-in user.
 * Call after a successful login / after loading the auth context.
 */
export function setUserContext(user: {
  id: string;
  email?: string;
  role?: string;
  tenantId?: string | number;
} | null): void {
  if (!ENABLED) return;
  Sentry.setUser(user ? { id: user.id, email: user.email, role: user.role, tenantId: String(user.tenantId ?? '') } : null);
}

export { Sentry };
